import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  AlertTriangle,
  Camera01,
  Check,
  Loading01 as Loader,
  RefreshCw01,
  Trash01,
  X,
} from "@untitledui/icons"
import type { VehicleSide } from "@/lib/api/types"
import { cx } from "@/lib/utils/cx"
import {
  basicSideOf,
  shortSideLabel,
  SIDE_OPTIONS,
  type BasicSide,
  type QEPhoto,
} from "./types"
import { VERDICT_COPY } from "./photo-verdict"

// Quick Estimate 3 — photo editor.
//
// Bottom-anchored sheet on mobile, centered dialog on desktop. Used
// by the sides step (and last-minute edits on the send step). The
// photos step uses QualityEditor instead — this one is purely about
// "did the AI tag the right side?".
//
// The side picker is a 5-card visual: each card is a small top-down
// car silhouette with the relevant side highlighted. Replaces the
// old 13-chip family-grouped list — see types.ts for the rationale.

interface PhotoEditorProps {
  open: boolean
  photo: QEPhoto | null
  // True iff capture pipeline still has the File reference for this
  // photo (drives whether Retry is available for failed evals — only
  // reachable in legacy paths; failed photos shouldn't reach this
  // editor in the new flow).
  canRetry: boolean
  retrying: boolean
  onClose: () => void
  onChangeSide: (side: VehicleSide) => void
  onChangeDescription: (text: string) => void
  onRemove: () => void
  onRetake?: () => void
  onRetry?: () => void
  onSendWithoutTag?: () => void
}

export function PhotoEditor({
  open,
  photo,
  canRetry,
  retrying,
  onClose,
  onChangeSide,
  onChangeDescription,
  onRemove,
  onRetake,
  onRetry,
  onSendWithoutTag,
}: PhotoEditorProps) {
  const [description, setDescription] = useState(photo?.description ?? "")

  useEffect(() => {
    if (open && photo) setDescription(photo.description ?? "")
  }, [open, photo])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!photo) return null

  const failed = photo.verdict === "eval-failed"
  const verdictCopy = VERDICT_COPY[photo.verdict]
  const lowQuality =
    !failed &&
    photo.uploadedUrl.length > 0 &&
    !photo.qualified &&
    !photo.sendWithoutAiTag

  const flushDescription = () => {
    if (description !== (photo.description ?? "")) {
      onChangeDescription(description)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="absolute inset-0 bg-zinc-950/60 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className={cx(
              "relative z-10 w-full sm:max-w-md max-h-[92vh] overflow-y-auto",
              "bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-zinc-900/30",
            )}
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
          >
            <div className="sm:hidden pt-2 pb-1 flex justify-center">
              <span className="block w-10 h-1.5 rounded-full bg-zinc-200" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-5 sm:px-6 pt-3 sm:pt-5 pb-3">
              <div className="min-w-0">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400">
                  Edit photo
                </p>
                <h3 className="text-[16px] font-bold text-zinc-900 leading-tight truncate">
                  {failed
                    ? "Couldn't tag this photo"
                    : photo.sendWithoutAiTag
                      ? "Untagged photo"
                      : shortSideLabel(photo.detectedSide)}
                </h3>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="size-9 rounded-xl border border-zinc-200 hover:bg-zinc-50 grid place-items-center text-zinc-500 transition-colors duration-150 shrink-0"
              >
                <X className="size-4" aria-hidden />
              </button>
            </div>

            {/* Photo preview */}
            <div className="px-5 sm:px-6">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-zinc-200/70 bg-zinc-100">
                <img
                  src={photo.previewUrl}
                  alt={shortSideLabel(photo.detectedSide)}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {photo.sideOverridden && !failed && (
                  <span className="absolute top-2.5 right-2.5 px-2 h-6 inline-flex items-center rounded-full bg-indigo-500/95 text-white text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/30 shadow">
                    Side edited
                  </span>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="px-5 sm:px-6 py-5 space-y-5">
              {failed ? (
                <FailedBranch
                  message={verdictCopy.body}
                  canRetry={canRetry}
                  retrying={retrying}
                  onRetry={onRetry}
                  onSendWithoutTag={onSendWithoutTag}
                />
              ) : (
                <>
                  {lowQuality && (
                    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3.5 flex items-start gap-3">
                      <span className="size-7 rounded-lg bg-amber-100 grid place-items-center shrink-0 mt-0.5">
                        <AlertTriangle
                          className="size-3.5 text-amber-700"
                          aria-hidden
                        />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[13px] font-bold text-amber-900 leading-tight">
                          {verdictCopy.title}
                        </p>
                        <p className="text-[12.5px] text-amber-800/85 leading-snug mt-0.5">
                          {verdictCopy.body}
                        </p>
                      </div>
                    </div>
                  )}

                  <SidePicker
                    current={basicSideOf(photo.detectedSide)}
                    onPick={onChangeSide}
                  />

                  {/* Description */}
                  <div>
                    <label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1.5 block">
                      Note for the shop (optional)
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      onBlur={flushDescription}
                      placeholder="Driver-side dent, paint cracked along the seam."
                      rows={3}
                      maxLength={400}
                      className="w-full rounded-2xl bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-[13.5px] text-zinc-800 placeholder:text-zinc-400 leading-relaxed resize-none outline-none focus:bg-white focus:border-zinc-400 transition-colors duration-150"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Action row — primary (Done / Remove for failed) sits
                full-width at the bottom on mobile so it lands under
                the user's thumb. Secondaries flow above in a wrap
                row. Inline on desktop. */}
            <div className="border-t border-zinc-100 bg-white px-5 sm:px-6 py-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              {failed ? (
                <button
                  type="button"
                  onClick={onRemove}
                  className="h-11 w-full sm:w-auto sm:ml-auto px-4 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors duration-150"
                >
                  <Trash01 className="size-4" aria-hidden />
                  Remove
                </button>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 sm:contents">
                    <button
                      type="button"
                      onClick={onRemove}
                      className="h-11 px-4 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors duration-150"
                    >
                      <Trash01 className="size-4" aria-hidden />
                      Remove
                    </button>
                    {onRetake && (
                      <button
                        type="button"
                        onClick={onRetake}
                        className="h-11 px-4 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-800 text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors duration-150"
                      >
                        <Camera01 className="size-4" aria-hidden />
                        Retake
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      flushDescription()
                      onClose()
                    }}
                    className="h-11 w-full sm:w-auto sm:ml-auto px-5 rounded-xl bg-zinc-900 hover:bg-black text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors duration-150"
                  >
                    <Check className="size-4" aria-hidden />
                    Done
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/* ── Failed-eval branch ───────────────────────────────────────── */

function FailedBranch({
  message,
  canRetry,
  retrying,
  onRetry,
  onSendWithoutTag,
}: {
  message: string
  canRetry: boolean
  retrying: boolean
  onRetry?: () => void
  onSendWithoutTag?: () => void
}) {
  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-rose-50 border border-rose-200 p-3.5 flex items-start gap-3">
        <span className="size-7 rounded-lg bg-rose-100 grid place-items-center shrink-0 mt-0.5">
          <AlertTriangle className="size-3.5 text-rose-700" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-rose-900 leading-tight">
            Couldn&rsquo;t tag this photo
          </p>
          <p className="text-[12.5px] text-rose-800/85 leading-snug mt-0.5">
            {message}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {canRetry && onRetry && (
          <button
            type="button"
            disabled={retrying}
            onClick={onRetry}
            className="h-11 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition-colors duration-150"
          >
            {retrying ? (
              <Loader className="size-4 animate-spin" aria-hidden />
            ) : (
              <RefreshCw01 className="size-4" aria-hidden />
            )}
            Retry tagging
          </button>
        )}
        {onSendWithoutTag && (
          <button
            type="button"
            onClick={onSendWithoutTag}
            className="h-11 px-4 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-800 text-[13px] font-semibold inline-flex items-center gap-1.5 transition-colors duration-150"
          >
            <Check className="size-4" aria-hidden />
            Use anyway
          </button>
        )}
      </div>
    </div>
  )
}

/* ── Visual side picker ───────────────────────────────────────── */

function SidePicker({
  current,
  onPick,
}: {
  current: BasicSide
  onPick: (side: BasicSide) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2.5">
        <label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
          Which side?
        </label>
        <span className="text-[11.5px] font-medium text-zinc-500">
          {SIDE_OPTIONS.find((o) => o.value === current)?.label ?? "—"}
        </span>
      </div>
      {/* 5 cards, one row. ≥56px wide so it fits 320px screens
          (320 - 40 padding - 24 gap = 256 / 5 ≈ 51px → readable at
          text-[10px], tap target stays ≥44px tall via min-h-[78px]). */}
      <div className="grid grid-cols-5 gap-1.5">
        {SIDE_OPTIONS.map((opt) => (
          <SideCard
            key={opt.value}
            side={opt.value}
            label={opt.label}
            selected={opt.value === current}
            onPick={() => onPick(opt.value)}
          />
        ))}
      </div>
    </div>
  )
}

function SideCard({
  side,
  label,
  selected,
  onPick,
}: {
  side: BasicSide
  label: string
  selected: boolean
  onPick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      className={cx(
        "relative rounded-2xl p-2 flex flex-col items-center justify-center gap-1.5",
        "min-h-[78px] transition-all duration-150",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
        selected
          ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/25 ring-2 ring-emerald-500"
          : "bg-zinc-50 hover:bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200",
      )}
    >
      <CarSideIcon side={side} active={selected} />
      <span className="text-[10px] font-bold uppercase tracking-wider leading-none">
        {label}
      </span>
    </button>
  )
}

// Small top-down car silhouette with the picked side highlighted.
// Inline SVG so we can drive every color from the parent's `active`
// state — no asset pipeline, no icon-pack stroke surprises.
function CarSideIcon({
  side,
  active,
}: {
  side: BasicSide
  active: boolean
}) {
  // Resolved colors — when active (selected), the card background is
  // emerald and the silhouette uses translucent white. When inactive,
  // the silhouette is zinc and the highlight is emerald-500 so it
  // pops against the zinc-50 card background.
  const body = active ? "rgba(255,255,255,0.18)" : "rgb(228 228 231)" // zinc-200
  const stroke = active ? "rgba(255,255,255,0.55)" : "rgb(212 212 216)" // zinc-300
  const accent = active ? "rgb(255 255 255)" : "rgb(16 185 129)" // emerald-500

  return (
    <svg
      viewBox="0 0 32 44"
      className="w-6 h-8"
      aria-hidden
      fill="none"
    >
      {/* Body — rounded rectangle with slight taper */}
      <rect
        x="6"
        y="3"
        width="20"
        height="38"
        rx="6"
        fill={body}
        stroke={stroke}
        strokeWidth="0.8"
      />
      {/* Hood / windshield seam */}
      <line
        x1="9"
        y1="14"
        x2="23"
        y2="14"
        stroke={stroke}
        strokeWidth="0.7"
      />
      {/* Trunk seam */}
      <line
        x1="9"
        y1="30"
        x2="23"
        y2="30"
        stroke={stroke}
        strokeWidth="0.7"
      />

      {/* Per-side highlight */}
      {side === "FRONT_CENTER" && (
        <path
          d="M6 9 V8 a6 6 0 0 1 6-5 h8 a6 6 0 0 1 6 5 V9 Z"
          fill={accent}
        />
      )}
      {side === "REAR_CENTER" && (
        <path
          d="M6 35 V36 a6 6 0 0 0 6 5 h8 a6 6 0 0 0 6-5 V35 Z"
          fill={accent}
        />
      )}
      {side === "LEFT_SIDE" && (
        <path
          d="M6 9 a6 6 0 0 1 3-5 V40 a6 6 0 0 1 -3-5 Z"
          fill={accent}
        />
      )}
      {side === "RIGHT_SIDE" && (
        <path
          d="M26 9 a6 6 0 0 0 -3-5 V40 a6 6 0 0 0 3-5 Z"
          fill={accent}
        />
      )}
      {side === "ROOF" && (
        <rect
          x="9"
          y="14"
          width="14"
          height="16"
          rx="2"
          fill={accent}
          opacity={active ? 0.85 : 0.9}
        />
      )}
    </svg>
  )
}
